import React from "react";
import ReactApexChart from "react-apexcharts";
import styled from "styled-components";

interface TeamPerformanceGraphProps {
  data: Array<{
    date: string;
    total_steps: number;
    participants: number;
  }>;
  loading?: boolean;
  challengeGoal: number;
  frequency: string;
}

const GraphContainer = styled.div`
  width: 90%;
  background: var(--card-background);
  border-radius: 12px;
  padding: 16px 16px 8px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 400px;
  margin-left: 1%;
`;

const GraphHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 8px;
`;

const GraphTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
`;

const LegendColor = styled.span<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: ${(props) => props.color};
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 370px;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
  opacity: 0.4;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-8px);
    }
  }
`;

const EmptyTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 4px 0;
  line-height: 1.5;
`;

const EmptyHint = styled.p`
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
  margin-top: 12px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6b7280;
  gap: 16px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(9, 71, 82, 0.1);
  border-top-color: #094752;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TeamPerformanceGraph: React.FC<TeamPerformanceGraphProps> = ({
  data,
  loading,
  challengeGoal,
  frequency,
}) => {
  // Loading state
  if (loading) {
    return (
      <GraphContainer>
        <GraphHeader>
          <GraphTitle>Daily Performance</GraphTitle>
        </GraphHeader>
        <LoadingContainer>
          <Spinner />
          <span>Loading performance data...</span>
        </LoadingContainer>
      </GraphContainer>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <GraphContainer>
        <GraphHeader>
          <GraphTitle>Daily Performance</GraphTitle>
        </GraphHeader>
        <EmptyStateContainer>
          <EmptyIcon>📊</EmptyIcon>
          <EmptyTitle>No Performance Data Available</EmptyTitle>
          <EmptyDescription>
            There's no team activity recorded for the selected date range.
          </EmptyDescription>
          <EmptyHint>
            Try selecting a different date range or check back when team members
            start logging activity.
          </EmptyHint>
        </EmptyStateContainer>
      </GraphContainer>
    );
  }

  // Format dates for display
  const categories = data.map((item) =>
    new Date(item.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
  );

  // Prepare series data
  const series =
    frequency === "daily"
      ? [
        {
          name: "Achieved",
          data: data.map((d) => d.total_steps),
        },
        {
          name: "Remaining",
          data: data.map((d) => Math.max(0, challengeGoal - d.total_steps)),
        },
      ]
      : [
        {
          name: "Achieved",
          data: data.map((d, i) => {
            const cumulative = data
              .slice(0, i + 1)
              .reduce((sum, item) => sum + item.total_steps, 0);
            return cumulative;
          }),
        },
        {
          name: "Remaining",
          data: data.map((d, i) => {
            const cumulative = data
              .slice(0, i + 1)
              .reduce((sum, item) => sum + item.total_steps, 0);
            return Math.max(0, challengeGoal - cumulative);
          }),
        },
      ];

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      toolbar: { show: false },
      fontFamily: "inherit",
      parentHeightOffset: 0,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        columnWidth: "60%",
      },
    },
    xaxis: {
      categories: categories,
      labels: {
        style: {
          fontSize: "12px",
          colors: "#6b7280",
        },
        offsetY: 0,
      },
      axisBorder: {
        show: true,
        color: "#e5e7eb",
        offsetY: 0,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => Math.round(Number(val)).toString(),
        style: {
          fontSize: "12px",
          colors: "#6b7280",
        },
        offsetX: 0,
      },
    },
    fill: {
      colors: ["#094752", "#CAFFFD"],
      opacity: 1,
    },
    colors: ["#094752", "#CAFFFD"],
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (value) => `${value}`,
      },
    },
    grid: {
      show: true,
      borderColor: "#f3f4f6",
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    noData: {
      text: "No data available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#6b7280",
        fontSize: "14px",
      },
    },
  };

  return (
    <GraphContainer>
      <GraphHeader>
        <GraphTitle>Daily Performance</GraphTitle>
        {data.length > 0 && (
          <Legend>
            <LegendItem>
              <LegendColor color="#094752" />
              Achieved
            </LegendItem>
            <LegendItem>
              <LegendColor color="#CAFFFD" />
              Remaining
            </LegendItem>
          </Legend>
        )}
      </GraphHeader>
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={370}
      />
    </GraphContainer>
  );
};

export default TeamPerformanceGraph;
